import React, { useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../Hooks';
import Loader from '../../Loader/Loader';
import { pageGetGallleryThunk } from '../../../Redux/thunks/pageDetailThunk';
import './GalleryModal.scss';

interface GalleryModalProps {
  show: boolean;
  onClose: () => void;
  title?: string;
}

const SLIDESHOW_INTERVAL = 2500;

const GalleryModal: React.FC<GalleryModalProps> = ({ show, onClose, title }) => {
  const dispatch = useDispatch();
  const pageDetailState = useAppSelector((state: any) => state.pagedetail.data);
  const pageGallleryData = useAppSelector((state: any) => state.pagedetail.gallleryData?.gallery || []);
  const galleryLoading = useAppSelector((state: any) => state.pagedetail.loading);
  const [isLoading, setIsLoading] = useState(false);
  // Only allow images and videos in slideshow
  const slideshowMedia = pageGallleryData.filter((m: any) => m.type === 'image' || m.type === 'video');
  const [preview, setPreview] = useState<null | any>(null);
  const [slideshow, setSlideshow] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);
  const slideshowRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (show && pageDetailState?._id) {
      setIsLoading(true);
      dispatch(pageGetGallleryThunk(pageDetailState._id, 1));
    }
    // eslint-disable-next-line
  }, [show, pageDetailState?._id]);

  useEffect(() => {
    if (!galleryLoading || (pageGallleryData && pageGallleryData.length > 0)) {
      setIsLoading(false);
    }
  }, [galleryLoading, pageGallleryData]);

  useEffect(() => {
    if (slideshow && slideshowMedia.length > 0) {
      setPreview(slideshowMedia[slideIndex]);
      slideshowRef.current = setTimeout(() => {
        setSlideIndex((prev) => (prev + 1) % slideshowMedia.length);
      }, SLIDESHOW_INTERVAL);
      return () => {
        if (slideshowRef.current) clearTimeout(slideshowRef.current);
      };
    }
  }, [slideshow, slideIndex, slideshowMedia.length]);

  useEffect(() => {
    if (!slideshow) {
      if (slideshowRef.current) clearTimeout(slideshowRef.current);
    }
  }, [slideshow]);

  const handleTileClick = (media: any) => {
    setPreview(media);
    setSlideshow(false);
    setSlideIndex(slideshowMedia.findIndex((m) => m._id === media._id));
  };

  const handlePreviewClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPreview(null);
    setSlideshow(false);
  };

  const handleSlideshow = () => {
    if (slideshowMedia.length > 0) {
      setSlideshow(true);
      setSlideIndex(0);
      setPreview(slideshowMedia[0]);
    }
  };

  const handleNext = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (slideshowMedia.length > 0) {
      setSlideIndex((prev) => (prev + 1) % slideshowMedia.length);
      setPreview(slideshowMedia[(slideIndex + 1) % slideshowMedia.length]);
    }
  };

  const handlePrev = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (slideshowMedia.length > 0) {
      setSlideIndex((prev) => (prev - 1 + slideshowMedia.length) % slideshowMedia.length);
      setPreview(slideshowMedia[(slideIndex - 1 + slideshowMedia.length) % slideshowMedia.length]);
    }
  };

  if (!show) return null;

  return (
    <div className="gallery-fullscreen-modal">
      <div className="gallery-modal-header">
        <h2>{title || 'Gallery'}</h2>
        <div className="gallery-modal-actions">
          <button className="gallery-slideshow-btn" onClick={handleSlideshow} disabled={slideshowMedia.length === 0}>
            <i className="fa fa-play-circle"></i> Slideshow
          </button>
          <button className="gallery-close-btn" onClick={onClose}>
            <i className="fa fa-times"></i>
          </button>
        </div>
      </div>
      <div className="gallery-modal-content">
        {isLoading && (
          <div className="gallery-modal-loader-overlay">
            <Loader />
          </div>
        )}
        <div className="gallery-modal-grid">
          {!isLoading && pageGallleryData.length === 0 ? (
            <div className="gallery-empty">No media found.</div>
          ) : (
            pageGallleryData.map((media: any, idx: number) => (
              <div className="gallery-modal-tile" key={media._id || idx} onClick={() => handleTileClick(media)}>
                {media.type === 'image' && (
                  <img src={media.url} alt={media._id} className="gallery-modal-img" />
                )}
                {media.type === 'video' && (
                  <div className="gallery-modal-video-thumb">
                    <img src={media.thumbnail || media.url} alt={media._id} className="gallery-modal-img" />
                    <span className="gallery-modal-video-icon"><i className="fa fa-play"></i></span>
                  </div>
                )}
                {media.type === 'audio' && (
                  <div className="gallery-modal-audio-thumb">
                    <i className="fa fa-butterflyheadphone"></i>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
      {preview && (
        <div className="gallery-preview-overlay" onClick={handlePreviewClose}>
          <div className="gallery-preview-modal" onClick={e => e.stopPropagation()}>
            <button className="gallery-preview-close" onClick={handlePreviewClose}>
              <i className="fa fa-times"></i>
            </button>
            {slideshow && (
              <div className="gallery-preview-controls">
                <button className="gallery-preview-prev" onClick={handlePrev} title="Previous">
                  <i className="fa fa-chevron-left"></i>
                </button>
                <button
                  className="gallery-preview-playpause"
                  onClick={() => setSlideshow((prev) => !prev)}
                  title={slideshow ? 'Pause' : 'Play'}
                >
                  <i className={`fa ${slideshow ? 'fa-pause' : 'fa-play'}`}></i>
                </button>
                <button className="gallery-preview-next" onClick={handleNext} title="Next">
                  <i className="fa fa-chevron-right"></i>
                </button>
              </div>
            )}
            {preview.type === 'image' && (
              <img src={preview.url} alt={preview._id} className="gallery-preview-img" />
            )}
            {preview.type === 'video' && (
              <video src={preview.url} controls className="gallery-preview-video" poster={preview.thumbnail} autoPlay={slideshow} />
            )}
            {preview.type === 'audio' && (
              <audio src={preview.url} controls className="gallery-preview-audio" autoPlay={slideshow} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryModal; 